import React, { useState, useEffect } from 'react';
import {
  Radar, Clock, TrendingUp, TrendingDown, Loader2, RefreshCw,
  Star, AlertCircle, CheckCircle, Zap, Globe, Filter,
  ChevronDown, ChevronUp, Target, Award, Settings, Sparkles
} from 'lucide-react';
import { useUser } from '@clerk/clerk-react';
import { useAppStore } from '../store';
import { useTranslation } from '../utils/translations';
import { getScannerLatest, getPresets } from '../utils/api';

// Trading sessions
const SESSIONS = {
  ASIA: { name: 'Asia', start: 0, end: 8, color: 'violet' },
  LONDON: { name: 'London', start: 8, end: 17, color: 'blue' },
  NEW_YORK: { name: 'New York', start: 13, end: 22, color: 'amber' },
  OVERLAP: { name: 'London/NY Overlap', start: 13, end: 17, color: 'emerald' }
};

function getCurrentSession() {
  const hour = new Date().getUTCHours();

  if (hour >= 13 && hour < 17) {
    return { ...SESSIONS.OVERLAP, isOverlap: true };
  } else if (hour >= 8 && hour < 17) {
    return { ...SESSIONS.LONDON, isOverlap: false };
  } else if (hour >= 13 && hour < 22) {
    return { ...SESSIONS.NEW_YORK, isOverlap: false };
  } else {
    return { ...SESSIONS.ASIA, isOverlap: false };
  }
}

function getNextScanTime() {
  const now = new Date();
  const minutes = now.getMinutes();
  const nextScanMinutes = Math.ceil(minutes / 15) * 15;
  const nextScan = new Date(now);
  nextScan.setMinutes(nextScanMinutes);
  nextScan.setSeconds(0);
  nextScan.setMilliseconds(0);
  
  // If next scan is at 60 minutes, move to next hour
  if (nextScanMinutes === 60) {
    nextScan.setHours(nextScan.getHours() + 1);
    nextScan.setMinutes(0);
  }
  
  return nextScan;
}

function formatTimeUntilNextScan() {
  const nextScan = getNextScanTime();
  const now = new Date();
  const diffMs = nextScan - now;
  const diffMins = Math.floor(diffMs / 60000);
  const diffSecs = Math.floor((diffMs % 60000) / 1000);
  
  if (diffMins > 0) {
    return `${diffMins}m ${diffSecs}s`;
  }
  return `${diffSecs}s`;
}

export default function SessionScanner() {
  const { t } = useTranslation();
  const { user } = useUser();
  const { activeTab, setSelectedPair, setActiveTab, setActiveStrategy, activeStrategy } = useAppStore();
  const [currentSession, setCurrentSession] = useState(getCurrentSession());
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [timeUntilNextScan, setTimeUntilNextScan] = useState(formatTimeUntilNextScan());
  const [expandedPair, setExpandedPair] = useState(null);
  const [currentPreset, setCurrentPreset] = useState('ICT_CORE');

  // Get user ID from Clerk
  const userId = user?.id || null;

  // Update session every minute
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentSession(getCurrentSession());
      setTimeUntilNextScan(formatTimeUntilNextScan());
    }, 1000);
    return () => clearInterval(interval);
  }, []);

  // Fetch current preset
  const fetchCurrentPreset = async () => {
    // PRIORITY 1: Use activeStrategy from global store if set
    if (activeStrategy) {
      const normalized = String(activeStrategy).toUpperCase().trim();
      console.log('📊 Scanner: Using activeStrategy from store:', normalized);
      setCurrentPreset(normalized);
      return normalized;
    }
    
    try {
      const response = await getPresets(userId);
      const data = response.data;
      const preset = data.current_preset || 'ICT_CORE';
      const normalizedPreset = preset ? String(preset).toUpperCase().trim() : 'ICT_CORE';
      setCurrentPreset(normalizedPreset);
      
      // ✅ Aggiorna lo store globale per coerenza con altri componenti
      setActiveStrategy(normalizedPreset);
      console.log('📊 Scanner: Fetched and set activeStrategy:', normalizedPreset);
      
      return normalizedPreset;
    } catch (err) {
      console.error("Error fetching current preset:", err);
      return 'ICT_CORE';
    }
  };

  // Fetch leaderboard and set up auto-refresh every 15 minutes
  const fetchLeaderboard = async (preset = null) => {
    try {
      const presetToUse = preset || currentPreset;
      const res = await getScannerLatest(presetToUse, userId);
      const data = res.data;
      if (data.success) {
        // Results are already sorted by confluence_score descending from backend
        const results = data.results || [];
        
        // Ensure each result has the preset from the response
        const presetFromResponse = data.preset || presetToUse;
        const enrichedResults = results.map(result => ({
          ...result,
          preset: result.preset || presetFromResponse, // Use result preset or fallback to response preset
          entry_threshold: result.entry_threshold || (
            presetFromResponse === 'FLIP_MODE' ? 75 : 
            presetFromResponse === 'BREAKOUT_MASTER' ? 85 : 
            presetFromResponse === 'LIQUIDITY_HUNTER' ? 80 : 
            70 // Default for ICT_CORE
          )
        }));
        
        setResults(enrichedResults);
        setLastUpdate(new Date());
        // Update current preset from response if available
        if (data.preset) {
          const normalizedPreset = String(data.preset).toUpperCase().trim();
          setCurrentPreset(normalizedPreset);
          // ✅ Aggiorna lo store globale per coerenza
          setActiveStrategy(normalizedPreset);
          console.log('📊 Scanner: Updated activeStrategy from response:', normalizedPreset);
        }
        
        console.log('📊 Leaderboard results with preset:', enrichedResults.map(r => ({
          symbol: r.symbol,
          preset: r.preset,
          score: r.confluence_score
        })));
      }
    } catch (err) {
      console.error("Error fetching leaderboard:", err);
    } finally {
      setLoading(false);
    }
  };

  // Initial fetch and set up auto-refresh every 15 minutes
  useEffect(() => {
    const loadData = async () => {
      // First get current preset
      const preset = await fetchCurrentPreset();
      // Then fetch leaderboard with that preset
      await fetchLeaderboard(preset);
    };
    
    loadData();
    
    // Refresh every 15 minutes (900000 ms)
    const interval = setInterval(() => {
      fetchLeaderboard();
    }, 15 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [userId]);

  // Reload leaderboard when preset changes (e.g., from Strategies page)
  useEffect(() => {
    if (activeTab === 'scanner') {
      fetchCurrentPreset().then(preset => {
        fetchLeaderboard(preset);
      });
    }
  }, [activeTab]);

  // Listen for strategy changes from Strategies page
  useEffect(() => {
    const handleStrategyChange = (event) => {
      const { preset } = event.detail;
      console.log('Strategy changed event received:', preset);
      setCurrentPreset(preset);
      fetchLeaderboard(preset);
    };

    window.addEventListener('strategy-changed', handleStrategyChange);
    return () => {
      window.removeEventListener('strategy-changed', handleStrategyChange);
    };
  }, []);

  const handleSelectPair = (symbol, result = null) => {
    setSelectedPair(symbol);
    
    // If result has preset, save it for AI Analysis coherence (normalize it)
    if (result && result.preset) {
      const normalizedPreset = String(result.preset).toUpperCase().trim();
      useAppStore.getState().setLeaderboardPreset(normalizedPreset);
      console.log('📊 Saved preset from leaderboard for Checklist:', result.preset, '-> normalized:', normalizedPreset);
    }
    
    setActiveTab('checklist');
  };
  
  const handleAIAnalysis = (result) => {
    setSelectedPair(result.symbol);
    
    // Save preset from leaderboard for coherence (normalize it)
    if (result.preset) {
      const normalizedPreset = String(result.preset).toUpperCase().trim();
      useAppStore.getState().setLeaderboardPreset(normalizedPreset);
      console.log('📊 Saved preset from leaderboard for AI Analysis:', result.preset, '-> normalized:', normalizedPreset);
    } else {
      console.warn('⚠️ No preset in result!', result);
    }
    
    setActiveTab('ai-analysis');
  };

  const nextSession = getCurrentSession();

  // Filter results by score (only show >= 65%)
  const topResults = results.filter(r => r.confluence_score >= 65);

  return (
    <div className="space-y-5 px-2 sm:px-0">
      {/* Session Header */}
      <div className="glass-card p-4 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5">
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-violet-500/30 to-purple-600/20 flex-shrink-0 border border-violet-500/40 shadow-soft">
              <Globe className="w-5 h-5 sm:w-6 sm:h-6 text-violet-300" />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-base sm:text-lg font-bold text-white flex items-center gap-2.5 tracking-tight">
                <Radar className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-400 flex-shrink-0" />
                <span className="truncate">{t('session_scanner')}</span>
              </h2>
              <p className="text-xs sm:text-sm text-slate-400 line-clamp-2 mt-1 leading-relaxed">
                Auto-scanner attivo ogni 15 minuti (08:00 - 00:00 ora italiana)
              </p>
            </div>
          </div>

          <div className="text-right flex-shrink-0">
            <div className={`px-3 py-1.5 rounded-full text-xs sm:text-sm font-bold inline-block shadow-soft ${
              currentSession.name === 'Asia' ? 'bg-violet-500/20 text-violet-300 border border-violet-500/40' :
              currentSession.name === 'London' ? 'bg-blue-500/20 text-blue-300 border border-blue-500/40' :
              currentSession.name === 'New York' ? 'bg-amber-500/20 text-amber-300 border border-amber-500/40' :
              'bg-emerald-500/20 text-emerald-300 border border-emerald-500/40'
            }`}>
              {currentSession.isOverlap ? '🔥 ' : ''}{currentSession.name}
            </div>
            <p className="text-[10px] text-slate-500 mt-1">
              Prossimo scan: {timeUntilNextScan}
            </p>
          </div>
        </div>

        {/* Current Time & Session Info */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 mb-5">
          {Object.entries(SESSIONS).map(([key, session]) => {
            const isActive = currentSession.name === session.name ||
              (currentSession.isOverlap && (session.name === 'London' || session.name === 'New York'));
            
            // Colori distintivi per ogni sessione
            const sessionColors = {
              'Asia': {
                bg: 'bg-violet-500/20',
                border: 'border-violet-500/50',
                text: 'text-violet-300',
                icon: 'violet'
              },
              'London': {
                bg: 'bg-blue-500/20',
                border: 'border-blue-500/50',
                text: 'text-blue-300',
                icon: 'blue'
              },
              'New York': {
                bg: 'bg-amber-500/20',
                border: 'border-amber-500/50',
                text: 'text-amber-300',
                icon: 'amber'
              },
              'London/NY Overlap': {
                bg: 'bg-emerald-500/20',
                border: 'border-emerald-500/50',
                text: 'text-emerald-300',
                icon: 'emerald'
              }
            };
            
            const colors = sessionColors[session.name] || sessionColors['Asia'];
            
            return (
              <div
                key={key}
                className={`p-3 rounded-lg text-center transition-smooth border ${
                  isActive
                    ? `${colors.bg} ${colors.border} shadow-soft`
                    : 'bg-slate-800/50 border-slate-700/30'
                }`}
              >
                <div className={`text-xs font-semibold ${isActive ? colors.text : 'text-slate-400'}`}>
                  {session.name}
                </div>
                <div className="text-[10px] text-slate-500 mt-0.5">
                  {session.start}:00 - {session.end}:00
                </div>
              </div>
            );
          })}
        </div>

        {/* Auto-Scanner Status */}
        <div className="px-3 sm:px-4 py-3 bg-gradient-to-r from-cyan-500/15 to-blue-500/10 rounded-lg border border-cyan-500/40 shadow-soft">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-2 sm:gap-3">
              <div className="relative flex-shrink-0">
                <Zap className="w-4 h-4 sm:w-5 sm:h-5 text-cyan-400" />
                <div className="absolute -top-1 -right-1 w-2 h-2 bg-cyan-400 rounded-full animate-pulse shadow-sm shadow-cyan-400/50" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-xs sm:text-sm font-bold text-cyan-400">Auto-Scanner Attivo</div>
                <div className="text-[10px] sm:text-xs text-slate-400 line-clamp-2">
                  Scan automatico ogni 15 minuti dalle 08:00 alle 00:00 (ora italiana)
                </div>
              </div>
            </div>
            <div className="text-right flex-shrink-0">
              <div className="text-[10px] sm:text-xs text-slate-500">Prossimo scan</div>
              <div className="text-xs sm:text-sm font-bold text-cyan-400 mt-1">{timeUntilNextScan}</div>
              {lastUpdate && (
                <div className="text-[10px] text-slate-500 mt-1">
                  Ultimo: {lastUpdate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="glass-card p-10 text-center">
          <div className="relative inline-block mb-5">
            <div className="absolute inset-0 bg-cyan-500/20 rounded-full blur-xl animate-pulse"></div>
            <Loader2 className="w-14 h-14 text-cyan-400 animate-spin mx-auto relative z-10" />
          </div>
          <h3 className="text-lg font-bold text-white mb-2 tracking-tight">Caricamento leaderboard...</h3>
          <p className="text-sm text-slate-400 leading-relaxed">
            Recupero risultati dall'ultimo scan automatico
          </p>
          <div className="mt-6 w-full max-w-xs mx-auto h-1 bg-slate-800/50 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-cyan-500 via-blue-500 to-cyan-500 rounded-full loading-shimmer"></div>
          </div>
        </div>
      )}

      {/* Leaderboard Section */}
      {!loading && (
        <div className="glass-card p-4 sm:p-5">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-5 sm:mb-6">
            <div className="flex items-center gap-3 min-w-0 flex-1">
              <Award className="w-6 h-6 sm:w-7 sm:h-7 text-amber-400 flex-shrink-0" />
              <div className="min-w-0 flex-1">
                <h3 className="text-base sm:text-lg font-bold text-white">TOP LEADERBOARD</h3>
                <p className="text-xs sm:text-sm text-slate-400 line-clamp-1">
                  Risultati ordinati per score di confluenza (≥65%)
                </p>
                <p className="text-[10px] text-amber-400/80 mt-1 italic">
                  ⚠️ Le percentuali sono somme di confluence, non probabilità di vittoria
                </p>
              </div>
            </div>
            <div className="text-right flex-shrink-0">
              <div className="text-3xl sm:text-4xl font-bold text-cyan-400">{topResults.length}</div>
              <div className="text-xs sm:text-sm text-slate-500">Setups trovati</div>
            </div>
          </div>

          {topResults.length === 0 ? (
            <div className="text-center py-8">
              <AlertCircle className="w-12 h-12 text-slate-500 mx-auto mb-3" />
              <h3 className="text-lg font-bold text-slate-400">Nessun setup trovato</h3>
              <p className="text-sm text-slate-500 mt-1">
                Lo scanner automatico non ha trovato setup con score ≥65%
              </p>
              <p className="text-xs text-slate-500 mt-2">
                Prossimo scan tra: {timeUntilNextScan}
              </p>
            </div>
          ) : (
            <div className="space-y-3 sm:space-y-2">
              {topResults.map((result, i) => {
                const isLong = result.direction?.toUpperCase() === 'LONG';
                return (
                <div
                  key={`${result.symbol}-${i}`}
                  className={`bg-gradient-to-r ${
                    i === 0 ? 'from-amber-500/20 to-amber-600/10 border-amber-500/50 shadow-medium shadow-amber-500/10' :
                    i === 1 ? 'from-slate-400/20 to-slate-500/10 border-slate-400/50 shadow-soft' :
                    i === 2 ? 'from-orange-500/20 to-orange-600/10 border-orange-500/50 shadow-soft shadow-orange-500/10' :
                    'from-emerald-500/10 to-emerald-600/5 border-emerald-500/30'
                  } rounded-xl border-2 overflow-hidden transition-smooth hover-lift`}
                >
                  <div
                    className="p-5 cursor-pointer hover:bg-white/10 transition-smooth interactive group"
                    onClick={() => setExpandedPair(expandedPair === result.symbol ? null : result.symbol)}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                      <div className="flex items-center gap-4 min-w-0 flex-1">
                        {/* Medal for top 3 */}
                        <div className="flex-shrink-0">
                          {i === 0 && <span className="text-4xl">🥇</span>}
                          {i === 1 && <span className="text-4xl">🥈</span>}
                          {i === 2 && <span className="text-4xl">🥉</span>}
                          {i > 2 && <span className="text-2xl font-bold text-white">{i + 1}.</span>}
                        </div>

                        <div className={`p-2.5 sm:p-3 rounded-xl flex-shrink-0 ${isLong ? 'bg-emerald-500/20' : 'bg-red-500/20'
                          }`}>
                          {isLong ? (
                            <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-400" />
                          ) : (
                            <TrendingDown className="w-5 h-5 sm:w-6 sm:h-6 text-red-400" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="flex items-center gap-2 sm:gap-3 flex-wrap mb-1">
                            <span className="font-bold text-white text-lg sm:text-xl truncate">{result.symbol}</span>
                            <span className={`text-xs sm:text-sm px-2 sm:px-2.5 py-1 sm:py-1.5 rounded-lg font-bold flex-shrink-0 ${isLong ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'
                              }`}>
                              {result.direction?.toUpperCase() || result.direction}
                            </span>
                          </div>
                          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                            <div className="flex flex-col">
                              <span className={`text-base sm:text-lg font-bold ${result.confluence_score >= 80 ? 'text-emerald-400' :
                                result.confluence_score >= 70 ? 'text-emerald-500' : 'text-amber-400'
                                }`}>
                                {result.confluence_score?.toFixed(1)}%
                              </span>
                              <span className="text-[9px] text-amber-400/70 italic">
                                Confluence Score
                              </span>
                            </div>
                            <span className="text-xs sm:text-sm text-slate-400">
                              {result.created_at ? new Date(result.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 sm:gap-4 flex-shrink-0">
                        {result.ai_analysis ? (
                          <div className="hidden sm:flex items-center gap-1.5 text-xs text-cyan-400 bg-cyan-500/10 px-3 py-1.5 rounded-full border border-cyan-500/20">
                            <Zap className="w-4 h-4" />
                            AI Analyzed
                          </div>
                        ) : (
                          <div className="hidden sm:block text-xs text-slate-500">Click to analyze</div>
                        )}
                        <div className="flex gap-2">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectPair(result.symbol, result);
                            }}
                            className="px-3.5 sm:px-4 py-2 sm:py-2.5 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white text-xs sm:text-sm font-bold rounded-xl hover:from-emerald-600 hover:to-emerald-700 transition-smooth whitespace-nowrap shadow-medium shadow-emerald-500/25 hover:shadow-strong hover:shadow-emerald-500/30 active:scale-95 focus-ring"
                          >
                            Checklist
                          </button>
                          {result.confluence_score >= 65 && (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleAIAnalysis(result);
                              }}
                              className="px-3.5 sm:px-4 py-2 sm:py-2.5 bg-gradient-to-r from-violet-500 to-violet-600 text-white text-xs sm:text-sm font-bold rounded-xl hover:from-violet-600 hover:to-violet-700 transition-smooth whitespace-nowrap shadow-medium shadow-violet-500/25 hover:shadow-strong hover:shadow-violet-500/30 active:scale-95 flex items-center gap-1.5 focus-ring"
                            >
                              <Sparkles className="w-3 h-3 sm:w-4 sm:h-4" />
                              AI
                            </button>
                          )}
                        </div>
                        {expandedPair === result.symbol ? (
                          <ChevronUp className="w-5 h-5 sm:w-6 sm:h-6 text-slate-400 flex-shrink-0" />
                        ) : (
                          <ChevronDown className="w-5 h-5 sm:w-6 sm:h-6 text-slate-400 flex-shrink-0" />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {expandedPair === result.symbol && result.ai_analysis && (
                    <div className="px-4 sm:px-5 pb-4 sm:pb-5 border-t border-white/10">
                      <div className="grid grid-cols-2 gap-3 sm:gap-4 mt-4">
                        {result.ai_analysis.entry && (
                          <div className="bg-slate-800/50 rounded-xl p-3 sm:p-4">
                            <div className="text-xs sm:text-sm text-slate-400 mb-2">Entry</div>
                            <div className="text-sm sm:text-base text-white font-bold">{result.ai_analysis.entry}</div>
                          </div>
                        )}
                        {result.ai_analysis.stop_loss && (
                          <div className="bg-slate-800/50 rounded-xl p-3 sm:p-4">
                            <div className="text-xs sm:text-sm text-slate-400 mb-2">Stop Loss</div>
                            <div className="text-sm sm:text-base text-red-400 font-bold">{result.ai_analysis.stop_loss}</div>
                          </div>
                        )}
                        {result.ai_analysis.take_profit && (
                          <div className="bg-slate-800/50 rounded-xl p-3 sm:p-4">
                            <div className="text-xs sm:text-sm text-slate-400 mb-2">Take Profit</div>
                            <div className="text-sm sm:text-base text-emerald-400 font-bold">{result.ai_analysis.take_profit}</div>
                          </div>
                        )}
                        {result.ai_analysis.risk_reward && (
                          <div className="bg-slate-800/50 rounded-xl p-3 sm:p-4">
                            <div className="text-xs sm:text-sm text-slate-400 mb-2">Risk/Reward</div>
                            <div className="text-sm sm:text-base text-cyan-400 font-bold">1:{result.ai_analysis.risk_reward}</div>
                          </div>
                        )}
                      </div>
                      {result.ai_analysis.analysis && (
                        <p className="text-sm sm:text-base text-slate-300 mt-4 leading-relaxed">{result.ai_analysis.analysis}</p>
                      )}
                    </div>
                  )}
                </div>
              );
              })}
            </div>
          )}
        </div>
      )}

      {/* Summary Stats */}
      {!loading && topResults.length > 0 && (
        <div className="glass-card p-5 sm:p-6">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-3">
            <div className="text-center p-3 sm:p-2 bg-slate-800/30 rounded-xl">
              <div className="text-3xl sm:text-4xl font-bold text-white">{topResults.length}</div>
              <div className="text-sm sm:text-xs text-slate-400 mt-1">Total Setups</div>
            </div>
            <div className="text-center p-3 sm:p-2 bg-emerald-500/10 rounded-xl">
              <div className="text-3xl sm:text-4xl font-bold text-emerald-400">
                {topResults.filter(r => r.direction?.toUpperCase() === 'LONG').length}
              </div>
              <div className="text-sm sm:text-xs text-slate-400 mt-1">BUY Setups</div>
            </div>
            <div className="text-center p-3 sm:p-2 bg-red-500/10 rounded-xl">
              <div className="text-3xl sm:text-4xl font-bold text-red-400">
                {topResults.filter(r => r.direction?.toUpperCase() === 'SHORT').length}
              </div>
              <div className="text-sm sm:text-xs text-slate-400 mt-1">SELL Setups</div>
            </div>
            <div className="text-center p-3 sm:p-2 bg-amber-500/10 rounded-xl">
              <div className="text-3xl sm:text-4xl font-bold text-amber-400">
                {topResults.length > 0 ? topResults[0].confluence_score?.toFixed(1) : '0'}%
              </div>
              <div className="text-sm sm:text-xs text-slate-400 mt-1">Top Score</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
